import React from 'react';
import { Link } from 'react-router-dom';
import { Navigation } from '@/components/navigation';
import { Footer } from '@/components/footer';
import { Button } from '@/components/ui/button';
import { PageBackground } from '@/components/ui/page-background';
import { PageHero } from '@/components/ui/page-hero';
import { ArrowRight, Target, Users, TrendingUp, Clock, Check, Briefcase, ShoppingCart, Building2 } from 'lucide-react';
import { useScrollReveal } from '@/hooks/use-scroll-reveal';
import { SEO } from '@/components/seo';

const principles = [
  {
    icon: Target,
    title: "Clear plan, no surprises",
    description: "Every project starts with defined scope, timeline, and success metrics."
  },
  {
    icon: Users,
    title: "Senior team on your project",
    description: "Work directly with experienced developers and designers, not junior staff."
  },
  {
    icon: TrendingUp,
    title: "Measurable results",
    description: "We track real outcomes: conversions, user engagement, and business impact."
  },
  {
    icon: Clock,
    title: "Weekly updates you can trust",
    description: "Consistent communication with demos, progress reports, and next steps."
  }
];

const clientValues = [
  "We speak human (no jargon)",
  "Predictable delivery (weekly demos)",
  "Post-launch partnership (we stay to help)",
  "Business outcomes first (not just pretty UIs)"
];

const workProcess = [
  {
    frequency: "Weekly",
    title: "Progress Updates",
    description: "Demo what we built, discuss next steps, address any questions.",
    number: "01"
  },
  {
    frequency: "Daily",
    title: "Internal Standups",
    description: "Our team stays aligned on priorities, blockers, and timeline.",
    number: "02"
  },
  {
    frequency: "Real-time",
    title: "Slack Communication",
    description: "Quick questions, approvals, and feedback without formal meetings.",
    number: "03"
  }
];

const exampleOutcomes = [
  {
    type: "Service business",
    outcome: "clearer copy + booking flow → more inquiries",
    label: "Example outcome",
    icon: Briefcase
  },
  {
    type: "E-commerce",
    outcome: "faster product pages + simplified checkout → fewer drop-offs",
    label: "Example outcome",
    icon: ShoppingCart
  },
  {
    type: "B2B",
    outcome: "focused landing pages → more qualified leads",
    label: "Example outcome",
    icon: Building2
  }
];

const About = () => {
  const { elementRef, isVisible, getRevealStyle } = useScrollReveal({ staggerDelay: 150 });

  const aboutSchema = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    "name": "About AffinityAim",
    "description": "Learn about AffinityAim, a web development company dedicated to engineering human connection through code.",
    "url": "https://www.affinityaim.com/about"
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://www.affinityaim.com"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "About",
        "item": "https://www.affinityaim.com/about"
      }
    ]
  };

  return (
    <PageBackground variant="aurora">
      <SEO
        title="About AffinityAim - Web Development Company Since 2020 | Our Story"
        description="Learn about AffinityAim, a web development company dedicated to engineering human connection through code. Meet our team and principles."
        canonical="https://www.affinityaim.com/about"
        keywords="about AffinityAim, web development company, our team, our mission"
        schema={[aboutSchema, breadcrumbSchema]}
      />
      <Navigation />

      <main id="main-content">
        <PageHero
          label="About Us"
          title="We build digital products"
          titleAccent="that actually work"
          description="AffinityAim is a premium app & web app studio founded in 2023. We help businesses grow through custom applications and conversion-focused websites."
        />

        {/* Mission & Principles - Premium Bento Grid */}
        <section className="py-24">
          <div className="container mx-auto px-6">
            <div className="max-w-6xl mx-auto">
              
              <div className="text-center mb-16">
                <div className="inline-flex items-center px-3 py-1 rounded-full border border-border bg-surface/50 mb-6">
                  <span className="text-[10px] font-semibold tracking-[0.1em] uppercase text-muted-foreground">
                    How We Work
                  </span>
                </div>
                <h2 className="font-display font-bold text-foreground mb-4" style={{ fontSize: 'clamp(28px, 4vw, 40px)', lineHeight: '1.2' }}>
                  Our principles
                </h2>
                <p className="text-muted-foreground max-w-xl mx-auto">
                  How we work with every client, on every project.
                </p>
              </div>

              <div ref={elementRef} className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {principles.map((principle, index) => {
                  const IconComponent = principle.icon;
                  return (
                    <div 
                      key={index}
                      className="group relative p-8 rounded-2xl bg-surface/50 border border-border hover:border-primary/30 transition-all duration-500 hover:shadow-lg hover:shadow-primary/5"
                      style={{
                        opacity: isVisible ? 1 : 0,
                        transform: isVisible ? 'translateY(0) scale(1)' : 'translateY(20px) scale(0.98)',
                        transition: 'opacity 0.6s cubic-bezier(0.16, 1, 0.3, 1), transform 0.7s cubic-bezier(0.16, 1, 0.3, 1)',
                        transitionDelay: `${index * 150}ms`,
                      }}
                    >
                      <div className="flex items-start gap-5">
                        <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-primary/20 to-primary/5 border border-primary/20 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-500">
                          <IconComponent className="w-6 h-6 text-primary" />
                        </div>
                        <div>
                          <h3 className="font-display font-semibold text-foreground mb-2 text-lg">
                            {principle.title}
                          </h3>
                          <p className="text-muted-foreground leading-relaxed">
                            {principle.description}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

            </div>
          </div>
        </section>

        {/* What clients value - Premium Chips */}
        <section className="py-24 bg-surface/30">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto">
              
              <div className="text-center mb-12">
                <h2 className="font-display font-bold text-foreground mb-4" style={{ fontSize: 'clamp(28px, 4vw, 40px)', lineHeight: '1.2' }}>
                  What clients value about working with us
                </h2>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {clientValues.map((value, index) => (
                  <div 
                    key={index} 
                    className="flex items-center gap-4 p-5 rounded-xl bg-surface/50 border border-border hover:border-primary/30 transition-all duration-300"
                    style={getRevealStyle(index + 4)}
                  >
                    <div className="w-8 h-8 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center flex-shrink-0">
                      <Check className="w-4 h-4 text-primary" />
                    </div>
                    <span className="text-foreground font-medium">{value}</span>
                  </div>
                ))}
              </div>

            </div>
          </div>
        </section>

        {/* How We Work - Timeline Style */}
        <section className="py-24">
          <div className="container mx-auto px-6">
            <div className="max-w-5xl mx-auto">
              
              <div className="text-center mb-16">
                <div className="inline-flex items-center px-3 py-1 rounded-full border border-border bg-surface/50 mb-6">
                  <span className="text-[10px] font-semibold tracking-[0.1em] uppercase text-muted-foreground">
                    Our Process
                  </span>
                </div>
                <h2 className="font-display font-bold text-foreground mb-4" style={{ fontSize: 'clamp(28px, 4vw, 40px)', lineHeight: '1.2' }}>
                  How we work
                </h2>
                <p className="text-muted-foreground max-w-xl mx-auto">
                  Structured process that keeps projects on track and clients informed.
                </p>
              </div>

              <div className="relative">
                {/* Timeline line */}
                <div className="hidden md:block absolute top-1/2 left-0 right-0 h-px bg-gradient-to-r from-transparent via-border to-transparent" />
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                  {workProcess.map((step, index) => (
                    <div 
                      key={index}
                      className="group relative"
                      style={getRevealStyle(index + 8)}
                    >
                      {/* Timeline dot */}
                      <div className="hidden md:flex absolute -top-3 left-1/2 -translate-x-1/2 w-6 h-6 rounded-full bg-background border-2 border-primary items-center justify-center">
                        <div className="w-2 h-2 rounded-full bg-primary" />
                      </div>
                      
                      <div className="p-8 rounded-2xl bg-surface/50 border border-border hover:border-primary/30 transition-all duration-500 hover:shadow-lg hover:shadow-primary/5 text-center md:mt-8">
                        <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-primary to-primary/60 text-primary-foreground font-bold text-lg mb-4">
                          {step.number}
                        </div>
                        <div className="text-primary font-bold text-xl mb-2">{step.frequency}</div>
                        <h3 className="font-medium text-foreground mb-2">{step.title}</h3>
                        <p className="text-sm text-muted-foreground">
                          {step.description}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

            </div>
          </div>
        </section>

        {/* Example outcomes - Bento Grid */}
        <section className="py-24 bg-surface/30">
          <div className="container mx-auto px-6">
            <div className="max-w-5xl mx-auto">
              
              <div className="text-center mb-16">
                <div className="inline-flex items-center px-3 py-1 rounded-full border border-border bg-surface/50 mb-6">
                  <span className="text-[10px] font-semibold tracking-[0.1em] uppercase text-muted-foreground">
                    Results
                  </span>
                </div>
                <h2 className="font-display font-bold text-foreground mb-4" style={{ fontSize: 'clamp(28px, 4vw, 40px)', lineHeight: '1.2' }}>
                  Example outcomes
                </h2>
                <p className="text-muted-foreground max-w-xl mx-auto">
                  These are example scenarios, not specific client metrics.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {exampleOutcomes.map((example, index) => {
                  const IconComponent = example.icon;
                  return (
                    <div 
                      key={index} 
                      className="group p-8 rounded-2xl bg-surface/50 border border-border hover:border-primary/30 transition-all duration-500 hover:shadow-lg hover:shadow-primary/5"
                      style={getRevealStyle(index + 11)}
                    >
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center">
                          <IconComponent className="w-5 h-5 text-primary" />
                        </div>
                        <span className="px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-primary bg-primary/10 rounded-full">
                          {example.label}
                        </span>
                      </div>
                      <h3 className="font-semibold text-foreground mb-3">{example.type}</h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {example.outcome}
                      </p>
                    </div>
                  );
                })}
              </div>

            </div>
          </div>
        </section>

        {/* CTA Section - Premium Gradient */}
        <section className="py-24 relative overflow-hidden">
          {/* Background gradient */}
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-primary/5 to-transparent pointer-events-none" />
          
          <div className="container mx-auto px-6 relative">
            <div className="max-w-3xl mx-auto text-center">
              
              <h2 className="font-display font-bold text-foreground mb-6" style={{ fontSize: 'clamp(32px, 5vw, 48px)', lineHeight: '1.1' }}>
                Ready to work together?
              </h2>
              <p className="text-muted-foreground mb-10 text-lg max-w-xl mx-auto">
                Let's discuss your project and see if we're a good fit.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/contact">
                  <Button size="lg" className="group bg-primary hover:bg-primary/90 text-primary-foreground px-8 h-14 text-base">
                    Start a project
                    <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
                <Link to="/services">
                  <Button variant="outline" size="lg" className="h-14 px-8 text-base border-border hover:bg-surface hover:border-primary/30">
                    View services
                  </Button>
                </Link>
              </div>

            </div>
          </div>
        </section>
      </main>

      <Footer />
    </PageBackground>
  );
};

export default About;
